import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  mergeClasses,
} from '@fluentui/react-components';

import { ApiError } from '../api/client';
import {
  MigrationDevice,
  RemoveDevicesResponseData,
  removeDevices,
} from '../api/migrations';
import BulkActionResultView, {
  DeviceActionResult,
  DeviceActionResultRow,
} from './BulkActionResultView';
import RelativeTime from './RelativeTime';
import StatusBadge from './StatusBadge';

import EnvironmentEcho from './EnvironmentEcho';
import { IDENTIFIER } from '../theme/tokens';
export interface ConfirmRemoveDevicesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  migrationId: string;
  migrationName: string;
  // Devices already selected on the parent's DeviceGrid. The modal renders
  // them as a read-only receipt — selection is not editable inside the modal.
  selectedDevices: MigrationDevice[];
  // Fires on Close if at least one device was successfully removed. The parent
  // wires this to invalidate queries + clear its selection state.
  onDevicesRemoved?: () => void;
}

const useStyles = makeStyles({
  body: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '16px',
    minWidth: '520px',
  },
  description: {
    fontSize: '13px',
    color: '#52525b',
  },
  receiptSurface: {
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: '#e4e4e7',
    borderRightColor: '#e4e4e7',
    borderBottomColor: '#e4e4e7',
    borderLeftColor: '#e4e4e7',
    borderTopLeftRadius: '6px',
    borderTopRightRadius: '6px',
    borderBottomLeftRadius: '6px',
    borderBottomRightRadius: '6px',
    maxHeight: '320px',
    overflowY: 'auto',
  },
  serialCell: {
    ...IDENTIFIER,
  },
  lastEventCell: {
    fontSize: '13px',
    color: '#52525b',
  },
  // Destructive primary button. Intentionally NOT using the brand-token cascade
  // — destructive actions stay semantic red across any future brand recolor.
  // Griffel supports nested `&:hover` / `&:active` via `:hover` / `:active`
  // keys directly on the style object.
  destructive: {
    backgroundColor: '#b91c1c',
    color: '#ffffff',
    borderTopColor: '#b91c1c',
    borderRightColor: '#b91c1c',
    borderBottomColor: '#b91c1c',
    borderLeftColor: '#b91c1c',
    ':hover': {
      backgroundColor: '#991b1b',
      borderTopColor: '#991b1b',
      borderRightColor: '#991b1b',
      borderBottomColor: '#991b1b',
      borderLeftColor: '#991b1b',
      color: '#ffffff',
    },
    ':active': {
      backgroundColor: '#7f1d1d',
      borderTopColor: '#7f1d1d',
      borderRightColor: '#7f1d1d',
      borderBottomColor: '#7f1d1d',
      borderLeftColor: '#7f1d1d',
      color: '#ffffff',
    },
    ':disabled': {
      backgroundColor: '#fca5a5',
      borderTopColor: '#fca5a5',
      borderRightColor: '#fca5a5',
      borderBottomColor: '#fca5a5',
      borderLeftColor: '#fca5a5',
      color: '#ffffff',
    },
  },
});

export default function ConfirmRemoveDevicesModal({
  open,
  onOpenChange,
  migrationId,
  migrationName,
  selectedDevices,
  onDevicesRemoved,
}: ConfirmRemoveDevicesModalProps) {
  const styles = useStyles();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<DeviceActionResult | null>(null);
  const [error, setError] = useState<unknown>(null);

  // Reset modal state every time the dialog is dismissed so a re-open starts
  // fresh. Fluent Dialog unmounts DialogSurface when closed, but the modal
  // itself stays mounted — this guard is the one that matters.
  useEffect(() => {
    if (!open) {
      setIsSubmitting(false);
      setResult(null);
      setError(null);
    }
  }, [open]);

  const count = selectedDevices.length;
  const serials = selectedDevices.map((d) => d.serialNumber);

  const handleConfirm = async () => {
    if (count === 0 || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await removeDevices(migrationId, serials);
      setResult(normalizeRemoveResult(response.data, response.message));
    } catch (err) {
      setError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Only fire onDevicesRemoved when the user closes AFTER a successful
    // remove. The parent clears its selection state on that signal — if we
    // fire it on cancel, the parent would lose the user's selection for no
    // reason.
    const removedCount = result?.rows.filter((r) => r.intent === 'success').length ?? 0;
    if (removedCount > 0) onDevicesRemoved?.();
    onOpenChange(false);
  };

  const title = result
    ? count === 1
      ? 'Device removal complete'
      : 'Device removal complete'
    : `Remove ${count} device${count === 1 ? '' : 's'} from ${migrationName}`;

  return (
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent>
            {/* FD-422 — removal deregisters from ATC MINT as well as writing this
                deployment's tables, so naming the environment is not decoration. */}
            <div className="mb-4">
              <EnvironmentEcho />
            </div>
            {result ? (
              <BulkActionResultView result={result} />
            ) : error ? (
              <div className={styles.body}>
                <ErrorView error={error} />
                <Receipt devices={selectedDevices} styles={styles} />
              </div>
            ) : (
              <div className={styles.body}>
                <div className={styles.description}>
                  The following device{count === 1 ? '' : 's'} will be removed from this
                  migration. This action cannot be undone.
                </div>
                <Receipt devices={selectedDevices} styles={styles} />
              </div>
            )}
          </DialogContent>
          <DialogActions>
            {result ? (
              <Button appearance="primary" onClick={handleClose}>
                Close
              </Button>
            ) : (
              <>
                <Button
                  appearance="subtle"
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  appearance="primary"
                  className={mergeClasses(styles.destructive)}
                  onClick={handleConfirm}
                  disabled={count === 0 || isSubmitting}
                >
                  {isSubmitting
                    ? 'Removing…'
                    : `Remove ${count} device${count === 1 ? '' : 's'}`}
                </Button>
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

function Receipt({
  devices,
  styles,
}: {
  devices: MigrationDevice[];
  styles: ReturnType<typeof useStyles>;
}) {
  return (
    <div className={styles.receiptSurface}>
      <Table size="small">
        <TableHeader>
          <TableRow>
            <TableHeaderCell>Serial</TableHeaderCell>
            <TableHeaderCell>Status</TableHeaderCell>
            <TableHeaderCell>Last event</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {devices.map((d) => (
            <TableRow key={d.serialNumber}>
              <TableCell className={styles.serialCell}>{d.serialNumber}</TableCell>
              <TableCell>
                <StatusBadge status={d.status} />
              </TableCell>
              <TableCell className={styles.lastEventCell}>
                {d.lastEventType ? (
                  <>
                    <span className="font-display">{d.lastEventType}</span>
                    {' · '}
                    <RelativeTime iso={d.lastEventTimestamp} />
                  </>
                ) : (
                  <span className="text-zinc-400">—</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ErrorView({ error }: { error: unknown }) {
  const title =
    error instanceof ApiError ? `Request failed (${error.status})` : 'Unexpected error';
  const body =
    error instanceof Error ? error.message : 'An unknown error occurred. Please try again.';
  return (
    <MessageBar intent="error">
      <MessageBarBody>
        <MessageBarTitle>{title}</MessageBarTitle>
        {body}
      </MessageBarBody>
    </MessageBar>
  );
}

// Mirrors normalizeRemoveResult from the deleted RemoveDevicesDialog so the
// result-view rendering stays pixel-identical between Assign (still
// BulkDeviceActionDialog) and Remove (this modal).
function normalizeRemoveResult(
  data: RemoveDevicesResponseData,
  message: string | null,
): DeviceActionResult {
  const rows: DeviceActionResultRow[] = [];

  for (const serial of data.removed ?? []) {
    rows.push({ serialNumber: serial, result: 'Removed', detail: null, intent: 'success' });
  }
  for (const serial of data.notFound ?? []) {
    rows.push({ serialNumber: serial, result: 'Not found', detail: null, intent: 'warning' });
  }
  for (const r of data.indexFailures ?? []) {
    rows.push({
      serialNumber: r.serialNumber,
      result: 'Index failure',
      detail: r.reason,
      intent: 'danger',
    });
  }
  // The customer-side removal succeeded even when MINT deregistration failed —
  // a stale MINT row self-heals on the next add (FD-ADR-016) — so this is a
  // warning alongside the device's Removed row, not a failure.
  for (const r of data.mintFailures ?? []) {
    rows.push({
      serialNumber: r.serialNumber,
      result: 'MINT deregistration failed',
      detail: r.reason,
      intent: 'warning',
    });
  }

  return {
    rows,
    summaryMessage:
      message ??
      `${data.removedCount} removed${data.notFoundCount > 0 ? `, ${data.notFoundCount} not found` : ''}.`,
  };
}
